<script lang="ts">
  /**
   * PrivateAppShell — production private-app layout. Frames every
   * `(private)/*` route with a workspace sidebar + lifted inset card,
   * mirroring the `Sidebar.Provider` + `Sidebar.Inset` pattern the
   * legacy `PrivateNavbarLayout` used.
   *
   * Originally lifted from the `/showcase` AppShell so the visual chrome
   * (TeamSwitcher brand, sidebar search, env banner footer, search modal)
   * stays identical — only the data wiring is real now.
   */
  import type { components } from '$api/schema';
  import { goto, invalidateAll } from '$app/navigation';
  import { page } from '$app/state';
  import type { HessianClient } from '$lib/client/hessianClient.svelte';
  import EditOrganizationDialog from '$lib/components/edit-organization-dialog.svelte';
  import * as Tooltip from '$lib/components/ui/tooltip';
  import {
  	getOrganizationContext,
  	organizationCookieName
  } from '$lib/context/organizationContext.svelte';
  import HessianIcon from '$lib/icons/hessian-icon.svelte';
  import {
  	getEnvOptions,
  	resolveEnv,
  	resolveEnvLabel,
  	resolveEnvMeta
  } from '$lib/config/env-banner';
  import { envBanner } from '$lib/state/env-banner.svelte';
  import {
  	COOKIE_MAX_AGE,
  	getRootDomain,
  	navigateToOrgSubdomain,
  	persistOrgCookie
  } from '$lib/utils/domain';
  import { getInitialsFromUsername } from '$lib/utils/initialsFromUsername';
  import { markOrgSwitch } from '$lib/utils/org-transition';
  import {
  	Button, Drawer, EnvBanner,
  	type EnvBannerEnv, EnvSwitcher, FilterToken, Input,
  	Kbd, Label, Modal,
  	SearchBar,
  	SearchSuggestions, SidebarCollapseToggle, StatusGlyph, type SuggestionSection, TeamSwitcher,
  	UserNav
  } from '@hessianhq/ui';
  import type { SupabaseClient, User } from '@supabase/supabase-js';
  import { mode, setMode } from 'mode-watcher';
  import type { Snippet } from 'svelte';
  import { onMount } from 'svelte';
  import { toast } from '$lib/utils/toast.svelte';

  type NavItem = {
    id: string;
    label: string;
    href?: string;
    /** Inline SVG path (24×24 viewBox, currentColor stroke). */
    iconPath?: string;
  };

  type Props = {
    /** Supabase client used to sign out from the user menu. */
    supabase?: SupabaseClient;
    /** Currently signed-in Supabase user. Drives the user nav row. */
    user?: User | null;
    /** Active organization id (route subdomain). */
    organizationId?: string;
    /** Active organization record. Drives the TeamSwitcher brand. */
    organization?: components['schemas']['Organization'];
    /** TanStack query client wired to the Hessian API. */
    hessianClient?: HessianClient;
    /** Drop the lifted-card chrome on the inset (margin + border +
     *  rounded corners + 36px padding) so the page content extends
     *  edge-to-edge from the sidebar to the viewport right. Used by
     *  app-shell-style routes like /runs/[id] that own their own
     *  internal panel chrome. */
    flush?: boolean;
    /** Let the user drag the sidebar's right edge to resize. */
    resizable?: boolean;
    /** Drag bounds when resizable. */
    minWidth?: number;
    maxWidth?: number;
    /**
     * Deployment environment. Anything other than `'prod'` renders an
     * `EnvBanner` strip above the workspace shell so it's instantly
     * obvious you're not on production. Defaults to whatever
     * `PUBLIC_ENVIRONMENT` / `import.meta.env.MODE` resolves to.
     */
    env?: EnvBannerEnv;
    /** Region / build / git sha shown after the env-banner body. */
    envMeta?: string;
    /**
     * Skip the global ⌘K shortcut that opens the workspace search modal.
     * Used by the public splash showcase, which mounts the shell as a
     * static preview inside a 3D-rotated marketing container — opening
     * the (portalled) search modal from the home page would dump a
     * flat dialog onto the marketing page that the visitor can't close
     * (the splash blocks pointer events on the preview by design).
     */
    disableShortcuts?: boolean;
    children: Snippet;
  };

  // Inline 24×24 SVG paths. Kept (rather than swapping to Lucide) for
  // visual parity with the showcase shell this layout was forked from —
  // same hairline weight, same green-rail active styling.
  const DEFAULT_ITEMS: NavItem[] = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      href: '/dashboard',
      iconPath:
        'M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z'
    },
    {
      id: 'workflows',
      label: 'Workflows',
      href: '/workflows',
      iconPath:
        'M3 3h7v7H3zm11 0h7v7h-7zm0 11h7v7h-7zM10 6h4M6 10v4M17 10v4'
    },
    {
      id: 'runs',
      label: 'Runs',
      href: '/runs',
      iconPath:
        'M13 2L3 14h7v8l10-12h-7z'
    },
    {
      id: 'tasks',
      label: 'Tasks',
      href: '/tasks',
      // lucide `list-checks` icon — two check-mark glyphs flanking three
      // tick-rows. Five sub-paths combined into one `d` so it matches the
      // other entries' single-path shape.
      iconPath:
        'M3 17l2 2 4-4 M3 7l2 2 4-4 M13 6h8 M13 12h8 M13 18h8'
    },
    {
      id: 'agents',
      label: 'Agents',
      href: '/agents',
      iconPath:
        'M12 2a5 5 0 015 5v3h-2V7a3 3 0 00-6 0v3H7V7a5 5 0 015-5zM5 10h14a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2v-8a2 2 0 012-2zm5 6a2 2 0 104 0 2 2 0 00-4 0z'
    },
    {
      id: 'workers',
      label: 'Workers',
      href: '/workers',
      iconPath:
        'M2 6a1 1 0 011-1h18a1 1 0 011 1v3a1 1 0 01-1 1H3a1 1 0 01-1-1V6zM2 15a1 1 0 011-1h18a1 1 0 011 1v3a1 1 0 01-1 1H3a1 1 0 01-1-1v-3z'
    },
    {
      id: 'queues',
      label: 'Queue groups',
      href: '/queue_groups',
      iconPath:
        'M9 7a3 3 0 116 0 3 3 0 01-6 0zm-6 0a3 3 0 116 0 3 3 0 01-6 0zm12 0a3 3 0 116 0 3 3 0 01-6 0zm-9 10a3 3 0 116 0 3 3 0 01-6 0zm6 0a3 3 0 116 0 3 3 0 01-6 0z'
    }
  ];

  // Administration subsection — rendered as a collapsible group below
  // the Workspace / Storage / Developers lists. Houses surfaces aimed
  // at org admins (audit history, user invites + roles) so they aren't
  // mixed in with the day-to-day Workspace entries.
  const ADMINISTRATION_ITEMS: NavItem[] = [
    {
      id: 'audit',
      label: 'Audit logs',
      href: '/audit',
      iconPath:
        'M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 1.5L18.5 9H13V3.5zM8 13h8v2H8v-2zm0 4h5v2H8v-2z'
    },
    {
      id: 'users',
      label: 'User management',
      href: '/users',
      // lucide `users` icon — two figures: a main user front, a partial
      // figure behind. Four sub-paths combined into one `d`.
      iconPath:
        'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2 M9 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8 M22 21v-2a4 4 0 0 0-3-3.87 M16 3.13a4 4 0 0 1 0 7.75'
    }
  ];

  // Developers subsection — rendered as a collapsible group beneath the
  // Storage list. Keeps secrets / webhooks / integrations grouped so the
  // workspace nav doesn't sprawl.
  const DEVELOPERS_ITEMS: NavItem[] = [
    {
      id: 'secrets',
      label: 'Secrets',
      href: '/secrets',
      // lucide `key-round` icon — main path only (the tiny keyhole dot is a
      // filled circle which won't render in the sidebar's stroke-only SVG).
      iconPath:
        'M2.586 17.414A2 2 0 0 0 2 18.828V21a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h1a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h.172a2 2 0 0 0 1.414-.586l.814-.814a6.5 6.5 0 1 0-4-4z'
    },
    {
      id: 'webhooks',
      label: 'Webhooks',
      href: '/webhooks',
      // lucide `webhook` icon — combined into a single `d` (three independent
      // sub-paths kept absolute so they're equivalent to lucide's three
      // <path> elements).
      iconPath:
        'M18 16.98h-5.99c-1.1 0-1.95.94-2.48 1.9A4 4 0 0 1 2 17c.01-.7.2-1.4.57-2 M6 17l3.13-5.78c.53-.97.1-2.18-.5-3.1a4 4 0 1 1 6.89-4.06 M12 6l3.13 5.73C15.66 12.7 16.9 13 18 13a4 4 0 0 1 0 8'
    },
    {
      id: 'integrations',
      label: 'Integrations',
      href: '/integrations',
      // lucide `blocks` icon — two subpaths approximating the stacked-block
      // glyph used elsewhere for integrations.
      iconPath:
        'M10 22V12a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2zM22 2h-8a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2z'
    }
  ];

  // Storage subsection — rendered as a collapsible group beneath the
  // main workspace list. Keeps Assets / Artifacts / Prompts together so
  // the workspace nav doesn't sprawl.
  const STORAGE_ITEMS: NavItem[] = [
    {
      id: 'assets',
      label: 'Assets',
      href: '/assets',
      // lucide `package` icon — three subpaths combined into a single `d`.
      iconPath:
        'M3 7l9 4 9-4-9-4-9 4z M3 7v10l9 4 9-4V7 M12 11v10'
    },
    {
      id: 'artifacts',
      label: 'Artifacts',
      href: '/artifacts',
      // lucide `files` icon — two subpaths.
      iconPath:
        'M9 4h7l3 3v11a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z M5 8v12a2 2 0 0 0 2 2h10'
    },
    {
      id: 'prompts',
      label: 'Prompts',
      href: '/prompts',
      iconPath:
        'M4 4h12l4 4v12H4V4zm12 0v4h4M7 12h10M7 16h7M7 8h4'
    }
  ];

  // Env banner config lives in $lib/config/env-banner.ts — see that
  // file for the .env var contract (PUBLIC_ENVIRONMENT / PUBLIC_ENV_LABEL
  // / PUBLIC_ENV_REGION / PUBLIC_BUILD_SHA / PUBLIC_ENV_META). All
  // resolvers fail closed to 'local' so the safety strip stays visible
  // when config is missing.

  // `organizationId` + `hessianClient` are accepted as props (so the
  // layout load can pass them through) but the shell itself doesn't
  // consume them yet — they're plumbed for upcoming workspace search
  // and team-switcher wiring.
  let {
    supabase,
    user,
    organizationId,
    organization,
    hessianClient,
    flush = false,
    resizable = true,
    minWidth = 180,
    maxWidth = 380,
    env = resolveEnv(),
    // svelte-ignore state_referenced_locally — `env` is the resolved
    // default; once defaulted, deriving the meta from it is safe.
    envMeta = resolveEnvMeta(env),
    disableShortcuts = false,
    children
  }: Props = $props();

  // ── Env switcher (popover in the EnvBanner action slot) ──────────
  // The prop is the *initial* env; clicking the switcher updates a
  // local state so the user can swap envs without round-tripping.
  // Picking `prod` hides the banner outright (matches real product
  // behavior — prod has no warning strip).
  // TODO-API: env switch — currently a UI-only toggle, no real switch.
  // svelte-ignore state_referenced_locally
  let currentEnv = $state<EnvBannerEnv>(env);
  // Only the `local` env should appear in the banner — sibling envs
  // (prod / staging / preview / dev / review) are intentionally hidden
  // so the banner is a dev-machine affordance only, not a switcher
  // surface for real environments.
  const envOptions = $derived(getEnvOptions(currentEnv).filter((o) => o.id === 'local'));
  const currentEnvOption = $derived(
    envOptions.find((o) => o.id === currentEnv) ?? envOptions[0]
  );
  // The active env still uses the deployment's `envMeta` (resolved from
  // .env vars); other envs in the switcher fall back to the static
  // defaults baked into the config module.
  const currentEnvMeta = $derived(
    currentEnv === env ? envMeta : (currentEnvOption.meta ?? envMeta)
  );
  const currentEnvLabel = $derived(resolveEnvLabel(currentEnv));

  // Publish the EnvBanner's placement (or null when hidden) to the
  // shared env-banner state so the root layout can offset the ToastDock
  // away from whichever edge the banner occupies. The placement is
  // hardcoded to 'bottom' below, but the signal carries it explicitly
  // so a future top-placed variant works without touching the dock.
  const bannerVisible = $derived(currentEnv === 'local' || currentEnv === 'dev');
  $effect(() => {
    envBanner.placement = bannerVisible ? 'bottom' : null;
    return () => {
      envBanner.placement = null;
    };
  });

  // Sidebar collapse — mirrors the docs Sidebar/SidebarInset pattern so
  // every page example carries the same collapse affordance.
  let collapsed = $state(false);
  let expandedWidth = $state(220);
  // 14px = hidden-rail (legacy). 56px gives just enough room for the
  // icon-rail variant: each nav row centres a 16px icon inside its 8px
  // gutter, the brand row hosts the 22px Hessian disc, and the team /
  // user pills shrink to a single 28px avatar.
  const COLLAPSED_RAIL_WIDTH = 56;
  const effectiveWidth = $derived(collapsed ? COLLAPSED_RAIL_WIDTH : expandedWidth);
  let dragging = $state(false);

  function startDrag(e: PointerEvent) {
    if (collapsed) return;
    const startX = e.clientX;
    const startWidth = expandedWidth;
    dragging = true;

    const onMove = (ev: PointerEvent) => {
      expandedWidth = Math.min(
        maxWidth,
        Math.max(minWidth, startWidth + (ev.clientX - startX))
      );
    };
    const onUp = () => {
      dragging = false;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    e.preventDefault();
  }

  function handleResizeKey(e: KeyboardEvent) {
    const step = e.shiftKey ? 32 : 8;
    if (e.key === 'ArrowLeft') {
      expandedWidth = Math.max(minWidth, expandedWidth - step);
      e.preventDefault();
    } else if (e.key === 'ArrowRight') {
      expandedWidth = Math.min(maxWidth, expandedWidth + step);
      e.preventDefault();
    } else if (e.key === 'Home') {
      expandedWidth = minWidth;
      e.preventDefault();
    } else if (e.key === 'End') {
      expandedWidth = maxWidth;
      e.preventDefault();
    }
  }

  // Derive the active nav item id from the current pathname. Prefix
  // matching (with the segment boundary) so /workflows/abc still
  // highlights "workflows" without /workers leaking into /workflows.
  const navItems = $derived(DEFAULT_ITEMS);

  // User management is hidden for personal organizations: the backend
  // redirects `/users` → `/dashboard` for personal orgs, so showing the
  // entry would be a dead end. Filter it out at the Administration
  // subsection source rather than hide-via-CSS so the icon-rail collapse
  // + keyboard nav both behave.
  const administrationItems = $derived(
    organization?.personal
      ? ADMINISTRATION_ITEMS.filter((item) => item.id !== 'users')
      : ADMINISTRATION_ITEMS
  );
  const current = $derived.by(() => {
    const path = page.url.pathname;
    if (path === '/dashboard' || path.startsWith('/dashboard/')) return 'dashboard';
    if (path === '/workflows' || path.startsWith('/workflows/')) return 'workflows';
    if (path === '/runs' || path.startsWith('/runs/')) return 'runs';
    if (path === '/tasks' || path.startsWith('/tasks/')) return 'tasks';
    if (path === '/agents' || path.startsWith('/agents/')) return 'agents';
    if (path === '/assets' || path.startsWith('/assets/')) return 'assets';
    if (path === '/artifacts' || path.startsWith('/artifacts/')) return 'artifacts';
    if (path === '/prompts' || path.startsWith('/prompts/')) return 'prompts';
    if (path === '/workers' || path.startsWith('/workers/')) return 'workers';
    if (path === '/queue_groups' || path.startsWith('/queue_groups/')) return 'queues';
    if (path === '/audit' || path.startsWith('/audit/')) return 'audit';
    if (path === '/users' || path.startsWith('/users/')) return 'users';
    if (path === '/secrets' || path.startsWith('/secrets/')) return 'secrets';
    if (path === '/webhooks' || path.startsWith('/webhooks/')) return 'webhooks';
    if (path === '/integrations' || path.startsWith('/integrations/')) return 'integrations';
    return '';
  });

  // Storage subsection open/closed state — default open so the items
  // are immediately visible. Auto-expanded any time the user is *on* a
  // storage page, so the active item is never hidden behind a closed
  // chevron. When the sidebar is in icon-rail mode the section label
  // is hidden by the same rule as `Workspace`, and the items are
  // forced visible regardless of `storageOpen` (see template).
  let storageOpen = $state(true);
  $effect(() => {
    if (
      current === 'assets' ||
      current === 'artifacts' ||
      current === 'prompts'
    ) {
      storageOpen = true;
    }
  });

  // Developers subsection open/closed state — defaults open and
  // auto-expands when the user is on a developer-tools page so the
  // active item is never hidden behind a closed chevron.
  let developersOpen = $state(true);
  $effect(() => {
    if (
      current === 'secrets' ||
      current === 'webhooks' ||
      current === 'integrations'
    ) {
      developersOpen = true;
    }
  });

  // Administration subsection open/closed state — defaults open. Auto-
  // expands when the user is on an admin surface (audit or user
  // management) so the active item is never hidden behind a closed
  // chevron.
  let administrationOpen = $state(true);
  $effect(() => {
    if (current === 'audit' || current === 'users') {
      administrationOpen = true;
    }
  });

  // ── TeamSwitcher + UserNav popover state ────────────────────────────
  let teamOpen = $state(false);
  let userOpen = $state(false);
  // Org logo upload dialog — triggered from the team-switcher menu, only
  // exposed for non-personal orgs (the backend rejects logo uploads on
  // personal orgs and the menu item is gated the same way).
  let showEditOrgDialog = $state(false);

  // Organization context — set upstream by `(private)/+layout.svelte`.
  // Mutating it (id/slug/personal) flips the cookie via the context's own
  // `$effect`, which in turn triggers child-page TanStack queries to
  // refetch (their queryKeys include `organization.id`). This mirrors the
  // pre-refactor `team-switcher.svelte` flow — without it, clicking a
  // different org on localhost left page data tied to the old org until
  // a full page reload.
  const selectedOrganization = getOrganizationContext();

  // Team list — fetched live from /v1/organizations. The popover renders
  // one row per org plus a "Create new team" CTA that opens the modal
  // below. Each row routes via subdomain swap on click.
  const organizationsQuery = $derived(
    hessianClient?.createQuery('get', '/v1/organizations', undefined)
  );

  // Brand-row state: the resolved `organization` prop is undefined while
  // the org list is in-flight OR if the request failed. We show a skeleton
  // / error UI in those cases instead of falling back to a literal
  // "workspace" placeholder.
  const orgsErrored = $derived(
    !organization && (organizationsQuery?.isError ?? false)
  );
  const workspaceMeta = $derived(
    organization ? `${organization.personal ? 'personal' : 'team'} · ${currentEnv}` : ''
  );

  const teams = $derived(
    (organizationsQuery?.data ?? []).map((org) => ({
      id: org.id,
      name: org.personal ? 'Personal Account' : org.name,
      plan: org.personal ? 'personal' : 'team',
      active: org.id === (organization?.id ?? organizationId),
      raw: org
    }))
  );

  /**
   * Switch to a different organization. Cross-subdomain switches go
   * through `navigateToOrgSubdomain`, which sets the cookie and does a
   * `location.href` jump so TanStack Query doesn't briefly refetch on
   * the old page before the new subdomain takes over.
   *
   * For same-host (local dev), mutate the organization context: its
   * `$effect` persists the cookie, and child-page queries refetch
   * because their queryKeys include `organization.id`. We also call
   * `invalidateAll` so the SSR-loaded `data.organizationId` (which
   * drives the TeamSwitcher trigger label and other layout-level
   * derived state) catches up to the new selection.
   */
  function handleOrgSelect(org: components['schemas']['Organization']) {
    // Skip the overlay if the user re-clicked the currently active org —
    // there's no navigation in flight, so flashing the "Switching to …"
    // chrome would feel like a bug.
    if (org.id === (organization?.id ?? organizationId)) {
      teamOpen = false;
      return;
    }

    const orgSlug = org.personal ? null : (org.slug ?? null);
    const displayName = org.personal ? 'Personal Account' : org.name;

    // Render the branded overlay immediately. The mounted
    // OrgTransitionOverlay listens for this synchronously so the
    // user sees feedback before the navigation kicks off; the cookie
    // half of the handoff lets the destination subdomain pick the
    // overlay back up on the other side of the redirect.
    markOrgSwitch({
      id: org.id,
      name: displayName,
      slug: orgSlug,
      initials: getInitialsFromUsername(org.name),
      logo: org.logo
    });

    const didNavigate = navigateToOrgSubdomain(
      org.id,
      orgSlug,
      organizationCookieName,
      COOKIE_MAX_AGE
    );
    if (!didNavigate) {
      // Set the cookie synchronously so the `invalidateAll` fetch below
      // sees it; we can't rely on the context's $effect — its microtask
      // may race with goto. Then mirror the change into the org context
      // so child-page TanStack queries (keyed by `organization.id`)
      // refetch with the new org.
      persistOrgCookie(
        org.id,
        organizationCookieName,
        COOKIE_MAX_AGE > 0 ? COOKIE_MAX_AGE : undefined
      );
      selectedOrganization.id = org.id;
      selectedOrganization.slug = orgSlug;
      selectedOrganization.personal = org.personal ?? false;

      // Tell the root `onNavigate` to keep the view-transition wrapper
      // active even though `from.pathname === to.pathname` — the org
      // switch is a real chrome change that benefits from the crossfade.
      window.__hessianForceViewTransition = true;
      goto(page.url.pathname + page.url.search, { invalidateAll: true });
    }
    teamOpen = false;
  }

  // ── Create new team modal ────────────────────────────────────────
  // The "+ Create new team" CTA opens this modal; on submit we POST to
  // /v1/organizations, refetch the org list, then jump to the new org's
  // subdomain so the user lands inside their freshly-created workspace.
  let showCreateDialog = $state(false);
  let newOrgName = $state('');
  let creatingOrg = $state(false);

  const createOrganization = $derived(
    hessianClient?.createMutation('post', '/v1/organizations')
  );

  // Slug preview — mirrors the server's slugifying rules so the user
  // sees what their subdomain URL will look like before submitting.
  const slugPreview = $derived(
    newOrgName
      ? newOrgName
          .toLowerCase()
          .replace(/[^a-z0-9\s-]/g, '')
          .replace(/\s+/g, '-')
          .replace(/-+/g, '-')
          .trim()
      : ''
  );

  // Root-domain preview for the slug → URL line in the modal. SSR has
  // no `window`, so default to a safe placeholder until hydration.
  const rootDomain = $derived(
    typeof window !== 'undefined' ? getRootDomain(window.location.hostname) : 'hessian.sh'
  );
  const isLocalhost = $derived(rootDomain === 'localhost' || rootDomain === '127.0.0.1');
  const urlProtocol = $derived(
    typeof window !== 'undefined' ? window.location.protocol.replace(':', '') : 'https'
  );

  function openCreateDialog() {
    newOrgName = '';
    showCreateDialog = true;
    teamOpen = false;
  }

  async function handleCreateOrg(e: SubmitEvent) {
    e.preventDefault();
    if (creatingOrg) return;
    const name = newOrgName.trim();
    if (!name) return;
    creatingOrg = true;
    try {
      const result = await createOrganization?.mutateAsync({
        body: { organization: { name } }
      });
      if (!result) {
        toast.error('Failed to create organization');
        return;
      }
      showCreateDialog = false;
      newOrgName = '';
      toast.success(`Created ${result.name}`);
      await organizationsQuery?.refetch();
      handleOrgSelect(result);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to create organization';
      toast.error(msg);
    } finally {
      creatingOrg = false;
    }
  }

  // Bottom UserNav menu — Account / Documentation / API keys / Theme / sep / Sign out.
  // Theme toggles through mode-watcher so it stays in sync with the
  // app-wide ModeWatcher and the cross-subdomain theme cookie.
  type UserMenuItem = {
    label: string;
    kbd?: string;
    sep?: boolean;
    onclick?: () => void;
  };

  const theme = $derived(mode.current === 'light' ? 'light' : 'dark');
  function toggleTheme() {
    setMode(theme === 'dark' ? 'light' : 'dark');
  }

  async function handleSignOut() {
    try {
      await supabase?.auth?.signOut();
    } finally {
      await goto('/login');
    }
  }

  // TODO: keyboard shortcuts — global gp/gs/gk bindings aren't wired yet,
  // so the kbd hints have been dropped from the user menu rows.
  const userMenuItems: UserMenuItem[] = $derived([
    { label: 'Account',       onclick: () => goto('/account') },
    { label: 'Documentation', onclick: () => goto('/docs') },
    { label: 'API keys',      onclick: () => goto('/api_keys') },
    // Show the current state in the kbd slot so the row reads as a
    // live toggle: "Theme · dark" → click → "Theme · light".
    { label: 'Theme', kbd: theme, onclick: toggleTheme },
    { label: 'Sign out', sep: true, onclick: handleSignOut }
  ]);

  // Prefer the metadata name; fall back to the local-part of the email
  // (`thomas.watson` rather than the whole `thomas.watson@acme.dev`) so
  // the UserNav avatar initials read as "TW" instead of "TS" — matches
  // the canonical showcase pattern.
  const userName = $derived(
    user?.user_metadata?.name ??
      (user?.email ? user.email.split('@')[0] : undefined) ??
      'User'
  );
  const userEmail = $derived(user?.email ?? '');
  // Supabase OAuth providers stash the avatar at `picture` (Google) or
  // `avatar_url` (GitHub, GitLab, …). Fall back across both; when
  // neither is set, UserNav falls back to its initials chip.
  const userAvatarUrl = $derived<string | undefined>(
    (user?.user_metadata?.picture as string | undefined) ??
      (user?.user_metadata?.avatar_url as string | undefined) ??
      undefined
  );

  // ── Mobile navigation (drawer) ────────────────────────────────────
  // Below the desktop sidebar's 900px breakpoint the sidebar is hidden;
  // a sticky top bar with a hamburger toggle opens this Drawer so phone
  // / small-tablet users still have access to the workspace nav, team
  // switcher, search, and user menu. Mirrors the docs-site pattern in
  // components/apps/docs/src/routes/+layout.svelte.
  let mobileNavOpen = $state(false);
  // Close the drawer on every navigation so the new page renders with
  // the drawer collapsed — same trick the docs mobile nav uses on each
  // link's onclick, lifted to a single page-state effect so we don't
  // have to wire every link by hand.
  $effect(() => {
    page.url.pathname;
    mobileNavOpen = false;
  });

  // ── Workspace search (modal) ──────────────────────────────────────
  // Sidebar carries a quick-search trigger that opens a Modal hosting
  // the SearchBar + filter tokens + a mocked results list. Drives the
  // "press ⌘K from anywhere" affordance the rest of the product has.
  let searchOpen = $state(false);
  let searchQuery = $state('');

  type SearchFilter = {
    k: string;
    v: string;
    negate?: boolean;
    range?: boolean;
  };
  let searchFilters = $state<SearchFilter[]>([
    { k: 'status', v: 'failed' },
    { k: 'created', v: '>7d', range: true }
  ]);

  type SearchResultStatus = 'completed' | 'running' | 'failed' | 'pending';
  type SearchResult = {
    kind: 'run' | 'workflow' | 'agent' | 'prompt';
    name: string;
    meta: string;
    status?: SearchResultStatus;
  };

  // TODO-API: workspace search — these results are static placeholders;
  // wire to a /search endpoint (runs + workflows + agents + prompts)
  // once the backend lands.
  const ALL_RESULTS: SearchResult[] = [
    { kind: 'run',      name: 'run.0188.a3f', meta: 'classify_and_route · 14:02Z',  status: 'failed' },
    { kind: 'run',      name: 'run.0187.812', meta: 'detect_pii_in_log · 13:58Z',   status: 'completed' },
    { kind: 'run',      name: 'run.0186.124', meta: 'auto_rotate_secrets · 13:31Z', status: 'running' },
    { kind: 'workflow', name: 'classify_and_route',  meta: '1,284 runs · P95 1.82s' },
    { kind: 'workflow', name: 'detect_pii_in_log',   meta: '812 runs · P95 4.21s'   },
    { kind: 'workflow', name: 'auto_rotate_secrets', meta: '124 runs · P95 6.84s'   },
    { kind: 'agent',    name: 'route_intent',  meta: 'sonnet-4-6 · 14 tools'     },
    { kind: 'agent',    name: 'triage_ticket', meta: 'opus-4-7 · 22 tools'       },
    { kind: 'prompt',   name: 'pii-detector',  meta: 'v1.4.2 · markdown · 612 tok' },
    { kind: 'prompt',   name: 'router-system', meta: 'v0.7.1 · markdown · 1.2k tok' }
  ];

  const filteredResults = $derived.by(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q && !searchFilters.length) return ALL_RESULTS;
    return ALL_RESULTS.filter((r) => {
      if (q && !r.name.toLowerCase().includes(q) && !r.meta.toLowerCase().includes(q))
        return false;
      // The mock applies a single filter rule: status:<value> narrows runs.
      for (const f of searchFilters) {
        if (f.k === 'status' && r.status) {
          const match = r.status === f.v;
          if (f.negate ? match : !match) return false;
        }
      }
      return true;
    });
  });

  const KIND_LABEL: Record<SearchResult['kind'], string> = {
    run: 'run',
    workflow: 'workflow',
    agent: 'agent',
    prompt: 'prompt'
  };

  function removeSearchFilter(i: number) {
    searchFilters = searchFilters.filter((_, idx) => idx !== i);
  }
  function clearAllSearch() {
    searchFilters = [];
    searchQuery = '';
  }

  // ── Suggestions popover (filter completions) ─────────────────────
  // Drives the autocomplete that surfaces below the SearchBar while the
  // input has focus. Picking a suggestion either pre-fills the input
  // with a `key:` prefix (so the user types a value) or attaches a
  // complete `key:value` as a new filter token.
  type Suggestion = {
    glyph?: string;
    label: string;
    desc?: string;
    kbd?: string;
  };

  // TODO-API: workspace search — suggestion sections are static seeds
  // for the autocomplete UI; replace with server-driven completions.
  const SUGGESTION_SECTIONS: SuggestionSection[] = [
    {
      label: 'Filter by',
      items: [
        { glyph: '◆', label: 'status:',   desc: 'completed / running / failed / pending', kbd: 'tab' },
        { glyph: '◆', label: 'workflow:', desc: 'classify_and_route / detect_pii_in_log / …', kbd: 'tab' },
        { glyph: '◆', label: 'kind:',     desc: 'run / workflow / agent / prompt',        kbd: 'tab' },
        { glyph: '◆', label: 'model:',    desc: 'sonnet-4-6 / opus-4-7 / haiku-4-5',      kbd: 'tab' },
        { glyph: '◆', label: 'created:',  desc: '>7d / >30d / today',                     kbd: 'tab' }
      ]
    },
    {
      label: 'Try',
      items: [
        { glyph: '↵', label: 'status:failed',     desc: 'narrow to failed runs' },
        { glyph: '↵', label: 'kind:workflow',     desc: 'show only workflows'   },
        { glyph: '↵', label: 'model:opus-4-7',    desc: 'opus-4-7 agents'       }
      ]
    }
  ];

  /** Flatten section items so the active index is a single number that
   *  the keyboard handler can step through linearly. */
  const flatSuggestions = $derived<Suggestion[]>(
    SUGGESTION_SECTIONS.flatMap((s) => s.items)
  );

  let inputFocused = $state(false);
  /** Suggestions are visible whenever the SearchBar input has focus —
   *  same convention as the standalone search demo. */
  const suggestionsVisible = $derived(searchOpen && inputFocused);

  let activeSuggestionIndex = $state(0);
  let activeResultIndex = $state(0);

  // Reset both indices whenever the underlying list changes so the
  // highlight never points at an out-of-range row.
  $effect(() => {
    searchQuery;
    activeSuggestionIndex = 0;
  });
  $effect(() => {
    const n = filteredResults.length;
    if (activeResultIndex >= n) activeResultIndex = Math.max(0, n - 1);
  });
  $effect(() => {
    if (!searchOpen) {
      activeResultIndex = 0;
      activeSuggestionIndex = 0;
      inputFocused = false;
    }
  });

  function selectSuggestion(idx: number) {
    const sug = flatSuggestions[idx];
    if (!sug) return;
    const [k, v] = sug.label.split(':');
    if (k && v) {
      // `key:value` — commit a new filter and clear the input.
      searchFilters = [...searchFilters, { k, v }];
      searchQuery = '';
    } else if (k) {
      // `key:` — prefill the input so the user can type the value.
      searchQuery = `${k}:`;
    }
  }

  function openResult(idx: number) {
    if (!filteredResults[idx]) return;
    // The demo doesn't navigate anywhere — just close the modal so the
    // gesture feels complete.
    searchOpen = false;
  }

  function moveActive(delta: number) {
    if (suggestionsVisible) {
      const n = flatSuggestions.length;
      if (!n) return;
      activeSuggestionIndex = (activeSuggestionIndex + delta + n) % n;
    } else {
      const n = filteredResults.length;
      if (!n) return;
      activeResultIndex = (activeResultIndex + delta + n) % n;
      // Keep the active row in view as the user scrolls past the
      // visible window of the list.
      queueMicrotask(() => {
        document
          .querySelector(`[data-search-result="${activeResultIndex}"]`)
          ?.scrollIntoView({ block: 'nearest' });
      });
    }
  }

  // Global ⌘K / Ctrl-K shortcut so the modal opens from anywhere.
  // The same listener handles in-modal arrow / enter / tab once open.
  // `disableShortcuts` opts out of the global ⌘K binding — see the
  // prop docs for why the splash showcase needs that escape hatch.
  onMount(() => {
    if (disableShortcuts) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        searchOpen = true;
        return;
      }
      if (!searchOpen) return;
      if (e.key === 'ArrowDown') {
        moveActive(1);
        e.preventDefault();
      } else if (e.key === 'ArrowUp') {
        moveActive(-1);
        e.preventDefault();
      } else if (e.key === 'Enter') {
        if (suggestionsVisible) selectSuggestion(activeSuggestionIndex);
        else openResult(activeResultIndex);
        e.preventDefault();
      } else if (e.key === 'Tab' && suggestionsVisible) {
        // Tab completes the highlighted suggestion's `key:` prefix
        // without closing the popover — matches the docs search demo.
        const sug = flatSuggestions[activeSuggestionIndex];
        if (sug) {
          const [k] = sug.label.split(':');
          if (k) searchQuery = `${k}:`;
        }
        e.preventDefault();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });
</script>

<div class="app-frame">
<!-- ── Mobile top bar ──────────────────────────────────────────────
     Only rendered under the 900px breakpoint (CSS-driven) — the desktop
     workspace sidebar is hidden there, so this bar gives phone / small-
     tablet users a hamburger to open the nav drawer, the Hessian brand
     mark as a home link, and a search affordance. Mirrors the docs-
     site pattern in components/apps/docs/src/routes/+layout.svelte. -->
<header class="app-mobile-topbar" aria-label="Mobile navigation">
  <button
    type="button"
    class="app-mobile-topbar-btn"
    onclick={() => (mobileNavOpen = true)}
    aria-label="Open navigation"
    aria-haspopup="dialog"
    aria-expanded={mobileNavOpen}
  >
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="1.6"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
    >
      <line x1="4" y1="7" x2="20" y2="7" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="17" x2="20" y2="17" />
    </svg>
  </button>
  <a class="app-mobile-topbar-brand" href="/dashboard" aria-label="Hessian home">
    <HessianIcon class="app-mobile-topbar-logo" />
    <span class="app-mobile-topbar-name">Hessian</span>
  </a>
  <button
    type="button"
    class="app-mobile-topbar-btn"
    onclick={() => (searchOpen = true)}
    aria-label="Search"
    aria-haspopup="dialog"
    aria-expanded={searchOpen}
  >
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="1.6"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
    >
      <circle cx="10" cy="10" r="6" />
      <path d="m14.5 14.5 5.5 5.5" />
    </svg>
  </button>
</header>

<div
  class="app-shell"
  class:app-shell-collapsed={collapsed}
  class:app-shell-resizing={dragging}
  style="--app-sb-width: {effectiveWidth}px;"
>
  <!-- ── Sidebar ─────────────────────────────────────────────────── -->
  <!-- `aria-hidden` deliberately omitted: collapsed mode still renders
       focusable controls (search trigger, nav links, team / user
       switchers), and hiding the aside from screen readers would block
       keyboard users mid-tab. The visual collapse is handled purely
       by CSS via `.app-shell-collapsed`; the toggle button below
       exposes the state via `aria-expanded`. -->
  <aside class="app-sb" aria-label="Workspace navigation">
    <!-- Hessian brand row — homepage link with logo + wordmark.
         Sits above the workspace switcher so the product identity is
         always visible regardless of which team the user is in. -->
    <a class="app-sb-hessian" href="/dashboard" aria-label="Hessian home">
      <HessianIcon class="app-sb-hessian-logo" />
      <span class="app-sb-hessian-name">Hessian</span>
    </a>

    <!-- Workspace brand row — TeamSwitcher composed with a switch menu.
         Until the `/v1/organizations` request resolves we show a skeleton
         row so the chrome doesn't flash a fake "workspace" label; if the
         request fails outright we surface an inline error + retry instead
         of silently hiding the failure. -->
    <div class="app-sb-brand">
      {#if organization}
        <TeamSwitcher
          bind:open={teamOpen}
          team={{ id: organization.id, name: organization.name, plan: workspaceMeta, logo: organization.logo }}
          matchTriggerWidth={!collapsed}
          placement={collapsed ? 'right-start' : 'bottom-start'}
        >
          {#snippet menu()}
            <div class="app-sb-menu" role="none">
              <div class="app-sb-menu-label">Switch team</div>
              {#each teams as t (t.id)}
                <button
                  type="button"
                  class="app-sb-menu-item"
                  role="menuitem"
                  onclick={() => handleOrgSelect(t.raw)}
                >
                  <span class="app-sb-menu-avatar" aria-hidden="true">
                    {#if t.raw.logo}
                      <img src={t.raw.logo} alt="" loading="lazy" />
                    {:else}
                      {t.name.slice(0, 1).toUpperCase()}
                    {/if}
                  </span>
                  <span class="app-sb-menu-meta">
                    <span class="app-sb-menu-name">{t.name}</span>
                    <span class="app-sb-menu-plan">{t.plan}</span>
                  </span>
                  {#if t.active}
                    <span class="app-sb-menu-active" aria-label="active">●</span>
                  {/if}
                </button>
              {/each}
              <div class="app-sb-menu-sep" aria-hidden="true"></div>
              {#if organization && !organization.personal && hessianClient}
                <button
                  type="button"
                  class="app-sb-menu-item app-sb-menu-cta"
                  role="menuitem"
                  onclick={() => {
                    showEditOrgDialog = true;
                    teamOpen = false;
                  }}
                >
                  <span class="app-sb-menu-plus" aria-hidden="true">
                    <svg
                      viewBox="0 0 24 24"
                      width="14"
                      height="14"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      aria-hidden="true"
                      focusable="false"
                    >
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                      <circle cx="8.5" cy="8.5" r="1.5" />
                      <polyline points="21 15 16 10 5 21" />
                    </svg>
                  </span>
                  <span>Edit profile picture</span>
                </button>
              {/if}
              <button
                type="button"
                class="app-sb-menu-item app-sb-menu-cta"
                role="menuitem"
                onclick={openCreateDialog}
              >
                <span class="app-sb-menu-plus" aria-hidden="true">+</span>
                <span>Create new team</span>
              </button>
            </div>
          {/snippet}
        </TeamSwitcher>
      {:else if orgsErrored}
        <div
          class="app-sb-brand-error"
          role="alert"
          title="Couldn't load workspaces"
        >
          <span class="app-sb-brand-error-glyph" aria-hidden="true">!</span>
          <span class="app-sb-brand-error-meta">
            <span class="app-sb-brand-error-title">Couldn't load workspaces</span>
            <button
              type="button"
              class="app-sb-brand-error-retry"
              onclick={() => organizationsQuery?.refetch()}
            >Retry</button>
          </span>
        </div>
      {:else}
        <div
          class="app-sb-brand-skeleton"
          aria-busy="true"
          aria-label="Loading workspace"
        >
          <span class="app-sb-brand-skeleton-avatar" aria-hidden="true"></span>
          <span class="app-sb-brand-skeleton-meta" aria-hidden="true">
            <span class="app-sb-brand-skeleton-name"></span>
            <span class="app-sb-brand-skeleton-plan"></span>
          </span>
        </div>
      {/if}
    </div>

    <!-- Quick-search trigger — opens the workspace search modal.
         Renders like a search-input affordance with a ⌘K hint chip on
         the right so the keyboard shortcut is discoverable. -->
    <button
      type="button"
      class="app-sb-search"
      onclick={() => (searchOpen = true)}
      aria-haspopup="dialog"
      aria-expanded={searchOpen}
    >
      <svg
        class="app-sb-search-lupe"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.6"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <circle cx="10" cy="10" r="6" />
        <path d="m14.5 14.5 5.5 5.5" />
      </svg>
      <span class="app-sb-search-text">Search…</span>
      <span class="app-sb-search-kbd" aria-hidden="true">⌘K</span>
    </button>

    <!-- Nav -->
    <nav class="app-sb-nav">
      <div class="app-sb-section-label">Workspace</div>
      <ul class="app-sb-list">
        {#each navItems as item (item.id)}
          {@const isActive = item.id === current}
          <li>
            <a
              class="app-sb-item"
              class:app-sb-item-active={isActive}
              href={item.href ?? '#'}
              aria-current={isActive ? 'page' : undefined}
            >
              <span class="app-sb-icon" aria-hidden="true">
                {#if item.iconPath}
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
                    <path d={item.iconPath} />
                  </svg>
                {/if}
              </span>
              <span class="app-sb-label">{item.label}</span>
            </a>
          </li>
        {/each}
      </ul>

      <!-- Storage subsection — collapsible. When the sidebar is in
           icon-rail mode the label hides (same rule as Workspace) and
           the items are forced visible. -->
      <button
        type="button"
        class="app-sb-section-label app-sb-section-toggle"
        aria-expanded={storageOpen}
        aria-controls="app-sb-storage-list"
        onclick={() => (storageOpen = !storageOpen)}
      >
        <span>Storage</span>
        <svg
          class="app-sb-section-chevron"
          class:app-sb-section-chevron-open={storageOpen}
          width="10"
          height="10"
          viewBox="0 0 10 10"
          aria-hidden="true"
        >
          <path
            d="M2.5 3.75 5 6.25 7.5 3.75"
            fill="none"
            stroke="currentColor"
            stroke-width="1.4"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
      {#if collapsed || storageOpen}
        <ul class="app-sb-list" id="app-sb-storage-list">
          {#each STORAGE_ITEMS as item (item.id)}
            {@const isActive = item.id === current}
            <li>
              <a
                class="app-sb-item"
                class:app-sb-item-active={isActive}
                href={item.href ?? '#'}
                aria-current={isActive ? 'page' : undefined}
              >
                <span class="app-sb-icon" aria-hidden="true">
                  {#if item.iconPath}
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
                      <path d={item.iconPath} />
                    </svg>
                  {/if}
                </span>
                <span class="app-sb-label">{item.label}</span>
              </a>
            </li>
          {/each}
        </ul>
      {/if}

      <!-- Developers subsection — collapsible. Mirrors Storage: label
           hides in icon-rail mode and items are forced visible. -->
      <button
        type="button"
        class="app-sb-section-label app-sb-section-toggle"
        aria-expanded={developersOpen}
        aria-controls="app-sb-developers-list"
        onclick={() => (developersOpen = !developersOpen)}
      >
        <span>Developers</span>
        <svg
          class="app-sb-section-chevron"
          class:app-sb-section-chevron-open={developersOpen}
          width="10"
          height="10"
          viewBox="0 0 10 10"
          aria-hidden="true"
        >
          <path
            d="M2.5 3.75 5 6.25 7.5 3.75"
            fill="none"
            stroke="currentColor"
            stroke-width="1.4"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
      {#if collapsed || developersOpen}
        <ul class="app-sb-list" id="app-sb-developers-list">
          {#each DEVELOPERS_ITEMS as item (item.id)}
            {@const isActive = item.id === current}
            <li>
              <a
                class="app-sb-item"
                class:app-sb-item-active={isActive}
                href={item.href ?? '#'}
                aria-current={isActive ? 'page' : undefined}
              >
                <span class="app-sb-icon" aria-hidden="true">
                  {#if item.iconPath}
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
                      <path d={item.iconPath} />
                    </svg>
                  {/if}
                </span>
                <span class="app-sb-label">{item.label}</span>
              </a>
            </li>
          {/each}
        </ul>
      {/if}

      <!-- Administration subsection — collapsible. Mirrors Storage /
           Developers: label hides in icon-rail mode and items are
           forced visible. Skip the whole subsection when filtering
           leaves it empty (e.g. a personal org with no team-level
           audit/users access — defensive; in practice 'audit' is
           always present). -->
      {#if administrationItems.length > 0}
        <button
          type="button"
          class="app-sb-section-label app-sb-section-toggle"
          aria-expanded={administrationOpen}
          aria-controls="app-sb-administration-list"
          onclick={() => (administrationOpen = !administrationOpen)}
        >
          <span>Administration</span>
          <svg
            class="app-sb-section-chevron"
            class:app-sb-section-chevron-open={administrationOpen}
            width="10"
            height="10"
            viewBox="0 0 10 10"
            aria-hidden="true"
          >
            <path
              d="M2.5 3.75 5 6.25 7.5 3.75"
              fill="none"
              stroke="currentColor"
              stroke-width="1.4"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
        {#if collapsed || administrationOpen}
          <ul class="app-sb-list" id="app-sb-administration-list">
            {#each administrationItems as item (item.id)}
              {@const isActive = item.id === current}
              <li>
                <a
                  class="app-sb-item"
                  class:app-sb-item-active={isActive}
                  href={item.href ?? '#'}
                  aria-current={isActive ? 'page' : undefined}
                >
                  <span class="app-sb-icon" aria-hidden="true">
                    {#if item.iconPath}
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
                        <path d={item.iconPath} />
                      </svg>
                    {/if}
                  </span>
                  <span class="app-sb-label">{item.label}</span>
                </a>
              </li>
            {/each}
          </ul>
        {/if}
      {/if}
    </nav>

    <!-- User nav footer -->
    <div class="app-sb-spacer"></div>
    <div class="app-sb-user">
      <UserNav
        bind:open={userOpen}
        user={{ name: userName, email: userEmail, src: userAvatarUrl, status: 'online' }}
        class="app-sb-user-trigger"
      >
        {#snippet menu()}
          <div class="app-sb-menu" role="none">
            {#each userMenuItems as it (it.label)}
              {#if it.sep}
                <div class="app-sb-menu-sep" aria-hidden="true"></div>
              {/if}
              <button
                type="button"
                class="app-sb-menu-item app-sb-menu-row"
                role="menuitem"
                onclick={it.onclick}
              >
                <span>{it.label}</span>
                {#if it.kbd}<span class="app-sb-menu-kbd">{it.kbd}</span>{/if}
              </button>
            {/each}
          </div>
        {/snippet}
      </UserNav>
    </div>

    {#if resizable && !collapsed}
      <!-- svelte-ignore a11y_no_noninteractive_tabindex — separator role takes focus for keyboard resize -->
      <!-- svelte-ignore a11y_no_noninteractive_element_interactions — separator role intentionally handles pointer+keyboard for resize gesture -->
      <div
        class="app-sb-resize-handle"
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize sidebar"
        aria-valuenow={expandedWidth}
        aria-valuemin={minWidth}
        aria-valuemax={maxWidth}
        tabindex="0"
        onpointerdown={startDrag}
        onkeydown={handleResizeKey}
      ></div>
    {/if}
  </aside>

  <!-- Collapse toggle — pinned to the seam between sidebar and inset
       (drawer-pull style), not inside the inset corner. Sits on the
       outer `.app-shell` so it isn't clipped by the inset's
       `overflow: auto`, and so page content never has to dodge it. -->
  <div class="app-sb-toggle-wrap">
    <SidebarCollapseToggle bind:collapsed />
  </div>

  <!-- ── Inset content card ──────────────────────────────────────── -->
  <!-- Wrap the page content with a Tooltip.Provider so shadcn-svelte
       Tooltip.Root usages inside existing components (e.g. the runs
       SearchControls trigger) can resolve their context. The previous
       layout did this implicitly via AppSidebar; the new shell uses
       @hessianhq/ui's own Tooltip (no provider needed) but children still
       reach for the bits-ui provider. -->
  <Tooltip.Provider>
    <div class="app-inset-shell">
      <!-- The `transition-authed` view-transition-name sits on the
           INNER content container, not the outer inset card. That way
           the dark card surrounding the content stays still during
           navigation (it's part of the root snapshot, which has
           `animation: none`) and only the content inside it cross-
           fades. -->
      <main
        class="app-inset"
        class:app-inset-flush={flush}
        data-slot="app-inset"
      >
        <div class="app-inset-inner transition-authed">
          {@render children()}
        </div>
      </main>
    </div>
  </Tooltip.Provider>
</div>

<!-- Environment banner — pinned to the bottom edge of the workspace
     shell. Visible on non-prod surfaces (`local` for dev-machine work
     and `dev` for the shared nonprod deployment at dev-hessian.sh) so
     operators can never mistake either for prod. Staging / preview /
     review / prod stay clean. -->
{#if currentEnv === 'local' || currentEnv === 'dev'}
  <EnvBanner
    class="hessian-app-envbanner"
    env={currentEnv}
    label={currentEnvLabel}
    meta={currentEnvMeta}
    placement="bottom"
  >
    {#if organization}<strong style="font-weight: 600;">{organization.name}</strong>&nbsp;·&nbsp;{/if}{currentEnvLabel}
    {#snippet action()}
      {#if currentEnv === 'local'}
        <EnvSwitcher
          options={envOptions}
          value={currentEnv}
          onChange={(id) => (currentEnv = id as EnvBannerEnv)}
          placement="top"
          compact
          showMeta
        />
      {/if}
    {/snippet}
  </EnvBanner>
{/if}
</div>

<!-- ── Workspace search modal ────────────────────────────────────────
     SearchBar carries the typed query + filter tokens (severity, status,
     created:range, …); the body lists mocked results below. Wired to the
     sidebar's quick-search trigger and the global ⌘K shortcut. -->
<Modal
  open={searchOpen}
  onClose={() => (searchOpen = false)}
  title="Search workspace"
  width={640}
>
  <div class="app-search-body">
    <SearchBar
      bind:value={searchQuery}
      placeholder="Filter runs by workflow:, status:, model:, cost:, created: …"
      count="{filteredResults.length} matches"
      onClear={clearAllSearch}
      onfocus={() => (inputFocused = true)}
      onblur={() => {
        // Delay so clicks on a suggestion register before the popover
        // collapses — same trick used in the standalone search demo.
        setTimeout(() => (inputFocused = false), 140);
      }}
    >
      {#snippet tokens()}
        {#each searchFilters as t, i (t.k + ':' + t.v + ':' + i)}
          <FilterToken
            k={t.k}
            v={t.v}
            negate={t.negate}
            range={t.range}
            onRemove={() => removeSearchFilter(i)}
          />
        {/each}
      {/snippet}
    </SearchBar>

    {#if suggestionsVisible}
      <div class="app-search-suggestions">
        <SearchSuggestions
          typed={searchQuery}
          sections={SUGGESTION_SECTIONS}
          activeIndex={activeSuggestionIndex}
        />
      </div>
    {/if}

    <div class="app-search-results" role="listbox" aria-label="Search results">
      {#if filteredResults.length === 0}
        <div class="app-search-empty">no matches · try clearing a filter</div>
      {:else}
        {#each filteredResults as r, i (r.kind + ':' + r.name)}
          <button
            type="button"
            class="app-search-row"
            class:app-search-row-active={i === activeResultIndex && !suggestionsVisible}
            role="option"
            aria-selected={i === activeResultIndex && !suggestionsVisible}
            data-search-result={i}
            onclick={() => openResult(i)}
            onmouseenter={() => (activeResultIndex = i)}
          >
            <span class="app-search-row-kind">{KIND_LABEL[r.kind]}</span>
            <span class="app-search-row-name">{r.name}</span>
            <span class="app-search-row-meta">{r.meta}</span>
            {#if r.status}
              <span class="app-search-row-status">
                <StatusGlyph status={r.status === 'completed' ? 'done' : r.status === 'pending' ? 'queued' : r.status} />
                {r.status}
              </span>
            {/if}
          </button>
        {/each}
      {/if}
    </div>

    <footer class="app-search-foot">
      <span><Kbd>↑↓</Kbd> navigate</span>
      <span><Kbd>↵</Kbd> {suggestionsVisible ? 'add filter' : 'open'}</span>
      <span><Kbd>tab</Kbd> complete key</span>
      <span style="margin-left: auto;"><Kbd>esc</Kbd> close</span>
    </footer>
  </div>
</Modal>

<!-- ── Mobile navigation drawer ────────────────────────────────────────
     Opens from the mobile top bar's hamburger button (which is only
     visible below the 900px breakpoint). Renders the same nav data the
     desktop sidebar uses — workspace / storage / developers / admin
     lists, plus the team-switcher and user-menu rows — inside a
     Drawer from @hessianhq/ui. The drawer auto-closes on navigation
     (page.url.pathname effect at the top of this file). -->
<Drawer
  bind:open={mobileNavOpen}
  side="left"
  width={300}
  title={organization?.name ?? 'Workspace'}
  idLabel={workspaceMeta || 'workspace'}
>
  <nav class="app-mobile-nav" aria-label="Workspace navigation">
    {#if organization && hessianClient && !organization.personal}
      <button
        type="button"
        class="app-mobile-nav-link"
        onclick={() => {
          showEditOrgDialog = true;
          mobileNavOpen = false;
        }}
      >Edit profile picture</button>
    {/if}

    {#if teams.length > 1}
      <div class="app-mobile-nav-eyebrow">Switch team</div>
      {#each teams as t (t.id)}
        <button
          type="button"
          class="app-mobile-nav-link"
          class:app-mobile-nav-link-active={t.active}
          onclick={() => handleOrgSelect(t.raw)}
        >
          <span class="app-mobile-nav-team-name">{t.name}</span>
          <span class="app-mobile-nav-team-plan">{t.plan}</span>
        </button>
      {/each}
    {/if}

    <div class="app-mobile-nav-eyebrow">Workspace</div>
    {#each navItems as item (item.id)}
      {@const isActive = item.id === current}
      <a
        class="app-mobile-nav-link"
        class:app-mobile-nav-link-active={isActive}
        href={item.href ?? '#'}
        aria-current={isActive ? 'page' : undefined}
      >
        <span class="app-mobile-nav-icon" aria-hidden="true">
          {#if item.iconPath}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
              <path d={item.iconPath} />
            </svg>
          {/if}
        </span>
        <span>{item.label}</span>
      </a>
    {/each}

    <div class="app-mobile-nav-eyebrow">Storage</div>
    {#each STORAGE_ITEMS as item (item.id)}
      {@const isActive = item.id === current}
      <a
        class="app-mobile-nav-link"
        class:app-mobile-nav-link-active={isActive}
        href={item.href ?? '#'}
        aria-current={isActive ? 'page' : undefined}
      >
        <span class="app-mobile-nav-icon" aria-hidden="true">
          {#if item.iconPath}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
              <path d={item.iconPath} />
            </svg>
          {/if}
        </span>
        <span>{item.label}</span>
      </a>
    {/each}

    <div class="app-mobile-nav-eyebrow">Developers</div>
    {#each DEVELOPERS_ITEMS as item (item.id)}
      {@const isActive = item.id === current}
      <a
        class="app-mobile-nav-link"
        class:app-mobile-nav-link-active={isActive}
        href={item.href ?? '#'}
        aria-current={isActive ? 'page' : undefined}
      >
        <span class="app-mobile-nav-icon" aria-hidden="true">
          {#if item.iconPath}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
              <path d={item.iconPath} />
            </svg>
          {/if}
        </span>
        <span>{item.label}</span>
      </a>
    {/each}

    {#if administrationItems.length > 0}
      <div class="app-mobile-nav-eyebrow">Administration</div>
      {#each administrationItems as item (item.id)}
        {@const isActive = item.id === current}
        <a
          class="app-mobile-nav-link"
          class:app-mobile-nav-link-active={isActive}
          href={item.href ?? '#'}
          aria-current={isActive ? 'page' : undefined}
        >
          <span class="app-mobile-nav-icon" aria-hidden="true">
            {#if item.iconPath}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
                <path d={item.iconPath} />
              </svg>
            {/if}
          </span>
          <span>{item.label}</span>
        </a>
      {/each}
    {/if}

    <div class="app-mobile-nav-eyebrow">{userName}</div>
    {#each userMenuItems as it (it.label)}
      <button
        type="button"
        class="app-mobile-nav-link app-mobile-nav-link-row"
        onclick={it.onclick}
      >
        <span>{it.label}</span>
        {#if it.kbd}<span class="app-mobile-nav-link-kbd">{it.kbd}</span>{/if}
      </button>
    {/each}
  </nav>
</Drawer>

<!-- ── Edit organization profile picture dialog ─────────────────────────
     Triggered from the TeamSwitcher popover's "Edit profile picture"
     entry. After a successful upload we refetch the org list (so the
     switcher reflects the new logo URL) and `invalidateAll` so the
     SSR-loaded `organization` prop on the layout picks up the new
     `logo` field too. -->
{#if organization && !organization.personal && hessianClient}
  <EditOrganizationDialog
    bind:open={showEditOrgDialog}
    {organization}
    {hessianClient}
    onUpdated={async () => {
      // The upload itself already succeeded by the time we get here —
      // failures from here on are purely refresh-side (network blip on
      // refetch, load-fn error during invalidateAll). Catch locally so
      // the dialog's outer try/catch doesn't surface a misleading
      // "Failed to update profile picture" toast on top of the success
      // toast it already fired.
      try {
        await organizationsQuery?.refetch();
        await invalidateAll();
      } catch (err) {
        console.error('Failed to refresh organization data after logo update', err);
        toast.error('Profile picture saved, but failed to refresh organization list. Reload to see the change.');
      }
    }}
  />
{/if}

<!-- ── Create new team modal ──────────────────────────────────────────
     Triggered from the TeamSwitcher popover's "+ Create new team" CTA.
     Submits to /v1/organizations and, on success, navigates to the new
     org's subdomain so the user lands inside their workspace. -->
<Modal
  open={showCreateDialog}
  onClose={() => (showCreateDialog = false)}
  title="Create a new team"
  width={480}
>
  <form class="app-create-team-form" onsubmit={handleCreateOrg}>
    <div class="app-create-team-field">
      <Label for="new-org-name">Organization Name</Label>
      <Input
        id="new-org-name"
        name="name"
        placeholder="Acme Corp"
        autocomplete="off"
        required
        bind:value={newOrgName}
      />
    </div>

    {#if slugPreview}
      <div class="app-create-team-slug">
        <p class="app-create-team-slug-label">Your organization URL will be:</p>
        <p class="app-create-team-slug-url">
          {urlProtocol}://<span class="app-create-team-slug-token">{slugPreview}</span>.{rootDomain}
        </p>
        {#if isLocalhost}
          <p class="app-create-team-slug-note">
            Note: subdomains don't work on localhost — switching teams will
            stay on this host until you're on a real subdomain environment.
          </p>
        {/if}
      </div>
    {/if}

    <div class="app-create-team-actions">
      <Button
        type="button"
        variant="secondary"
        onclick={() => (showCreateDialog = false)}
        disabled={creatingOrg}
      >
        Cancel
      </Button>
      <Button type="submit" disabled={creatingOrg || !newOrgName.trim()}>
        {creatingOrg ? 'Creating…' : 'Create team'}
      </Button>
    </div>
  </form>
</Modal>

<style>
  /* ── Create-team modal form ─────────────────────────────────────── */
  .app-create-team-form {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
  .app-create-team-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .app-create-team-slug {
    padding: 12px;
    border: 1px dashed color-mix(in oklab, var(--foreground) 14%, transparent);
    border-radius: 6px;
    background: color-mix(in oklab, var(--foreground) 2.5%, transparent);
  }
  .app-create-team-slug-label {
    font-size: 11px;
    color: var(--muted-foreground);
    margin: 0;
  }
  .app-create-team-slug-url {
    margin: 4px 0 0;
    font-family: var(--font-mono);
    font-size: 12.5px;
    color: var(--foreground);
    word-break: break-all;
  }
  .app-create-team-slug-token {
    color: var(--brand-green, oklch(0.7 0.18 145));
    font-weight: 600;
  }
  .app-create-team-slug-note {
    margin: 8px 0 0;
    font-size: 11px;
    font-style: italic;
    color: var(--muted-foreground);
  }
  .app-create-team-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 4px;
  }

  /* ── Frame ──────────────────────────────────────────────────────── */
  /* Outer frame — column flex so the EnvBanner sits above the
     sidebar/inset row. `100vh` lives on the frame; the shell beneath
     fills the remaining space. */
  .app-frame {
    display: flex;
    flex-direction: column;
    height: 100vh;
    background: var(--sidebar);
    /* Safety net: stop wide inset content (e.g. desktop-sized KPI grids
       in the dashboard example) from making the whole page horizontally
       scrollable on mobile. Per-page styles still handle responsive
       reflow within the inset; this just bounds the bleed. */
    max-width: 100vw;
    overflow-x: clip;
  }

  /* ── View transitions — pin shell chrome ─────────────────────────
     Naming the sidebar, collapse toggle, and env banner pulls them out
     of the root view-transition snapshot so they stay anchored while
     the `transition-authed` inset cross-fades between routes. The
     matching `animation-duration: 0s` rules in app.css ensure these
     pieces snap instantly to their new state (e.g. active nav rail
     moving with the route change) instead of cross-fading. */
  .app-sb {
    view-transition-name: hessian-app-sidebar;
  }
  .app-sb-toggle-wrap {
    view-transition-name: hessian-app-toggle;
  }
  :global(.hessian-app-envbanner) {
    view-transition-name: hessian-app-envbanner;
  }
  .app-shell {
    /* Routes that use AppShell opt out of the docs chrome (via
       `+layout.svelte`'s `isFullscreenPage` branch), so the shell
       sits at the viewport root and fills it edge-to-edge — same
       shape as hessian/web's `Sidebar.Provider` + `Sidebar.Inset`. */
    position: relative;
    display: flex;
    flex: 1;
    min-height: 0;
    min-width: 0;
    background: var(--sidebar);
  }
  /* Below the docs shell's mobile breakpoint, hide the simulated
     workspace sidebar so /pages/* examples get the full viewport for
     their inset content. The Topbar / breadcrumb inside each page
     still provides context. */
  @media (max-width: 900px) {
    .app-sb,
    .app-sb-toggle-wrap {
      display: none !important;
    }
  }
  /* Style hook for the "switch env" link in the env banner action slot. */
  :global(.app-envb-link) {
    font-family: var(--font-mono);
    font-size: 10.5px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--foreground);
    text-decoration: none;
    padding: 2px 8px;
    border: 1px solid color-mix(in oklab, var(--foreground) 18%, transparent);
    border-radius: 3px;
    transition: border-color 140ms cubic-bezier(0.4, 0, 0.2, 1),
                background 140ms cubic-bezier(0.4, 0, 0.2, 1);
  }
  :global(.app-envb-link:hover) {
    border-color: color-mix(in oklab, var(--foreground) 40%, transparent);
    background: color-mix(in oklab, var(--foreground) 6%, transparent);
  }

  /* Inset shell — flex peer of the sidebar, no chrome of its own. */
  .app-inset-shell {
    flex: 1;
    min-width: 0;
    position: relative;
    display: flex;
    min-height: 0;
  }

  /* Collapse toggle — drawer-pull pinned to the sidebar/inset seam.
     Sitting on the outer `.app-shell` (which is `position: relative`)
     means it tracks `--app-sb-width` as the sidebar resizes / collapses
     and never overlaps inset content — pages can lay out their
     breadcrumb and headers flush to the left without having to dodge
     a corner button. Vertically aligned with the TeamSwitcher row at
     the top of the sidebar so the eye reads the two as a single bar. */
  .app-sb-toggle-wrap {
    position: absolute;
    top: 22px;
    left: var(--app-sb-width, 220px);
    transform: translateX(-50%);
    z-index: 20;
    transition: left 220ms cubic-bezier(0.4, 0, 0.2, 1);
  }

  /* ── Sidebar ─────────────────────────────────────────────────────── */
  .app-sb {
    position: relative;
    /* `z-index: 2` lifts the sidebar's stacking context above the
       inset (`.app-inset` carries `transition-authed`, which itself
       creates a stacking context via `view-transition-name`). Without
       this, popups originating in the sidebar (TeamSwitcher,
       UserNav) get painted *under* the inset card because both
       contexts have z-auto and the inset is later in source order. */
    z-index: 2;
    width: var(--app-sb-width, 220px);
    flex-shrink: 0;
    padding: 14px 10px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    border-right: 1px solid var(--sidebar-border, color-mix(in oklab, var(--foreground) 8%, transparent));
    color: var(--foreground);
    overflow: hidden;
    transition: width 220ms cubic-bezier(0.4, 0, 0.2, 1),
                padding 220ms cubic-bezier(0.4, 0, 0.2, 1);
  }
  /* Drag tracks the cursor 1:1 — animation would lag the gesture. */
  .app-shell-resizing .app-sb,
  .app-shell-resizing .app-sb-toggle-wrap {
    transition: none;
  }

  .app-sb-resize-handle {
    position: absolute;
    top: 0;
    right: -3px;
    bottom: 0;
    width: 6px;
    cursor: col-resize;
    background: transparent;
    transition: background 140ms cubic-bezier(0.4, 0, 0.2, 1);
    touch-action: none;
    z-index: 5;
  }
  .app-sb-resize-handle:hover {
    background: color-mix(in oklab, var(--brand-green, oklch(0.7 0.18 145)) 45%, transparent);
  }
  .app-sb-resize-handle:focus-visible {
    outline: none;
    background: color-mix(in oklab, var(--brand-green, oklch(0.7 0.18 145)) 65%, transparent);
  }
  .app-shell-resizing .app-sb-resize-handle {
    background: var(--brand-green, oklch(0.7 0.18 145));
  }

  /* ── Collapsed (icon-rail) variant ────────────────────────────────
     Instead of hiding the whole sidebar, narrow it to a 56px rail and
     show just icons / avatars. Each row centres its glyph and hides
     its accompanying label / meta / caret. */
  .app-shell-collapsed .app-sb {
    padding: 14px 4px;
    /* Keep the right-edge separator visible so the rail still reads as
       chrome distinct from the inset card. */
  }
  /* Brand row: hide the wordmark, keep just the disc, centre it. */
  .app-shell-collapsed .app-sb-hessian {
    justify-content: center;
    padding: 2px 0 12px;
  }
  .app-shell-collapsed .app-sb-hessian-name {
    display: none;
  }
  /* Workspace switcher: hide name / plan / caret, keep just the avatar. */
  .app-shell-collapsed .app-sb-brand {
    padding-bottom: 12px;
  }
  :global(.app-shell-collapsed .app-sb-brand .hk-ts-trigger) {
    justify-content: center;
    padding: 6px 0;
    gap: 0;
  }
  :global(.app-shell-collapsed .app-sb-brand .hk-ts-meta),
  :global(.app-shell-collapsed .app-sb-brand .hk-ts-caret) {
    display: none;
  }
  /* Skeleton + error variants collapse the same way — hide the text
     column, centre the 28px avatar/glyph in the rail. */
  .app-shell-collapsed .app-sb-brand-skeleton,
  .app-shell-collapsed .app-sb-brand-error {
    justify-content: center;
    padding: 6px 0;
    gap: 0;
  }
  .app-shell-collapsed .app-sb-brand-skeleton-meta,
  .app-shell-collapsed .app-sb-brand-error-meta {
    display: none;
  }
  /* Quick-search trigger: hide the label and the ⌘K hint, and shrink
     the trigger to a square so the visible border doesn't stretch to
     the rail width. */
  .app-shell-collapsed .app-sb-search {
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    margin: 0 auto 12px;
    gap: 0;
  }
  .app-shell-collapsed .app-sb-search-text,
  .app-shell-collapsed .app-sb-search-kbd {
    display: none;
  }
  /* Workspace section label only carries text, so it disappears. */
  .app-shell-collapsed .app-sb-section-label {
    display: none;
  }
  /* Nav items: hide label, centre the icon, drop the active-rail
     gutter so the icon sits at the geometric centre of the row. */
  .app-shell-collapsed .app-sb-item {
    justify-content: center;
    padding: 8px 0;
    gap: 0;
  }
  .app-shell-collapsed .app-sb-label {
    display: none;
  }
  .app-shell-collapsed .app-sb-item-active::before {
    left: 0;
  }
  /* User nav: hide name / email / caret, keep the avatar centred. */
  :global(.app-shell-collapsed .app-sb-user .hk-un-trigger) {
    justify-content: center;
    padding: 4px 0;
    gap: 0;
  }
  :global(.app-shell-collapsed .app-sb-user .hk-un-meta),
  :global(.app-shell-collapsed .app-sb-user .hk-un-caret) {
    display: none;
  }
  /* The resize handle and section-label don't make sense on the rail. */
  .app-shell-collapsed .app-sb-resize-handle {
    display: none;
  }

  /* Hessian wordmark — pinned to the very top of the sidebar so the
     product identity reads first, with the team switcher below. */
  .app-sb-hessian {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 2px 4px 12px;
    margin-bottom: 10px;
    border-bottom: 1px dashed color-mix(in oklab, var(--foreground) 8%, transparent);
    color: var(--foreground);
    text-decoration: none;
    transition: opacity 140ms cubic-bezier(0.4, 0, 0.2, 1);
  }
  .app-sb-hessian:hover {
    opacity: 0.78;
  }
  :global(.app-sb-hessian-logo) {
    width: 22px;
    height: 22px;
    flex-shrink: 0;
  }
  .app-sb-hessian-name {
    font-family: var(--font-sans);
    font-size: 15px;
    font-weight: 600;
    letter-spacing: -0.02em;
    line-height: 1;
  }

  /* Brand row wrapper — adds the dashed separator and spacing. The
     TeamSwitcher component supplies its own border and avatar chrome. */
  .app-sb-brand {
    padding: 0 0 12px;
    border-bottom: 1px dashed color-mix(in oklab, var(--foreground) 8%, transparent);
    margin-bottom: 10px;
  }
  /* Override the TeamSwitcher's brand-green tinted avatar — the org
     switcher should read as neutral chrome, not an accented "live"
     element. Mirrors the muted treatment used by `.app-sb-menu-avatar`
     in the popover so the trigger and the open list look uniform. */
  :global(.app-sb-brand .hk-ts-avatar) {
    border-color: color-mix(in oklab, var(--foreground) 14%, transparent);
    background: color-mix(in oklab, var(--foreground) 5%, transparent);
    color: var(--foreground);
  }

  /* Brand-row skeleton — placeholder shown while `/v1/organizations` is
     resolving. Geometry mirrors the TeamSwitcher trigger (8/10 padding,
     28px avatar, 13px / 9.5px text rows) so the row doesn't jump width
     or height once the real chrome lands. */
  .app-sb-brand-skeleton {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 8px 10px;
    border: 1px solid color-mix(in oklab, var(--foreground) 8%, transparent);
    border-radius: var(--radius);
  }
  .app-sb-brand-skeleton-avatar {
    width: 28px;
    height: 28px;
    flex-shrink: 0;
    border-radius: 3px;
    background: color-mix(in oklab, var(--foreground) 18%, transparent);
    animation: app-sb-brand-pulse 1.4s ease-in-out infinite;
  }
  .app-sb-brand-skeleton-meta {
    display: inline-flex;
    flex-direction: column;
    gap: 6px;
    flex: 1;
    min-width: 0;
  }
  .app-sb-brand-skeleton-name {
    height: 11px;
    width: 70%;
    border-radius: 3px;
    background: color-mix(in oklab, var(--foreground) 18%, transparent);
    animation: app-sb-brand-pulse 1.4s ease-in-out infinite;
  }
  .app-sb-brand-skeleton-plan {
    height: 8px;
    width: 40%;
    border-radius: 3px;
    background: color-mix(in oklab, var(--foreground) 12%, transparent);
    animation: app-sb-brand-pulse 1.4s ease-in-out infinite;
  }
  @keyframes app-sb-brand-pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.45; }
  }

  /* Brand-row error — shown when `/v1/organizations` fails. Keeps the
     same outer frame as the trigger so the sidebar layout stays stable,
     with a destructive-tinted glyph and a Retry button that refetches
     the query in place. */
  .app-sb-brand-error {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 8px 10px;
    border: 1px solid color-mix(in oklab, var(--destructive, oklch(0.62 0.22 25)) 45%, transparent);
    border-radius: var(--radius);
    background: color-mix(in oklab, var(--destructive, oklch(0.62 0.22 25)) 8%, transparent);
    color: var(--foreground);
  }
  .app-sb-brand-error-glyph {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    flex-shrink: 0;
    border-radius: 3px;
    border: 1px solid color-mix(in oklab, var(--destructive, oklch(0.62 0.22 25)) 55%, transparent);
    background: color-mix(in oklab, var(--destructive, oklch(0.62 0.22 25)) 18%, transparent);
    color: light-dark(oklch(0.45 0.18 25), oklch(0.86 0.12 25));
    font-family: var(--font-mono);
    font-size: 13px;
    font-weight: 700;
  }
  .app-sb-brand-error-meta {
    display: inline-flex;
    flex-direction: column;
    gap: 2px;
    flex: 1;
    min-width: 0;
  }
  .app-sb-brand-error-title {
    font-family: var(--font-sans);
    font-size: 12px;
    font-weight: 500;
    color: var(--foreground);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .app-sb-brand-error-retry {
    align-self: flex-start;
    padding: 0;
    border: 0;
    background: transparent;
    color: light-dark(oklch(0.45 0.18 25), oklch(0.86 0.12 25));
    font-family: var(--font-mono);
    font-size: 10px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    cursor: pointer;
  }
  .app-sb-brand-error-retry:hover {
    text-decoration: underline;
  }
  .app-sb-brand-error-retry:focus-visible {
    outline: 2px solid var(--brand-green, oklch(0.7 0.18 145));
    outline-offset: 2px;
  }

  /* Quick-search trigger — reads like a faux input so the affordance
     is obvious, with the ⌘K hint chip pinned to the right. */
  .app-sb-search {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    margin: 0 0 12px;
    padding: 6px 8px;
    border: 1px solid color-mix(in oklab, var(--foreground) 18%, transparent);
    border-radius: 4px;
    background: color-mix(in oklab, var(--foreground) 3%, transparent);
    color: var(--muted-foreground);
    font-family: var(--font-sans);
    font-size: 12.5px;
    text-align: left;
    cursor: pointer;
    transition: border-color 140ms cubic-bezier(0.4, 0, 0.2, 1),
                color 140ms cubic-bezier(0.4, 0, 0.2, 1),
                background 140ms cubic-bezier(0.4, 0, 0.2, 1);
  }
  .app-sb-search:hover {
    border-color: color-mix(in oklab, var(--foreground) 22%, transparent);
    background: color-mix(in oklab, var(--foreground) 5%, transparent);
    color: var(--foreground);
  }
  .app-sb-search:focus-visible {
    outline: 2px solid var(--brand-green, oklch(0.7 0.18 145));
    outline-offset: 2px;
  }
  .app-sb-search-lupe {
    width: 13px;
    height: 13px;
    color: currentColor;
    opacity: 0.8;
    flex-shrink: 0;
  }
  .app-sb-search-text {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .app-sb-search-kbd {
    font-family: var(--font-mono);
    font-size: 10px;
    letter-spacing: 0.04em;
    padding: 1px 5px;
    border: 1px solid color-mix(in oklab, var(--foreground) 12%, transparent);
    border-radius: 3px;
    background: var(--background);
    color: var(--muted-foreground);
  }

  /* ── Search modal body ──────────────────────────────────────────── */
  :global(.app-search-body) {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }
  :global(.app-search-results) {
    display: flex;
    flex-direction: column;
    gap: 2px;
    max-height: 320px;
    overflow-y: auto;
    margin: 0 -4px;
    padding: 2px 4px;
  }
  :global(.app-search-row) {
    display: grid;
    grid-template-columns: 70px 1fr auto auto;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 8px 10px;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 4px;
    text-align: left;
    cursor: pointer;
    transition: background 120ms cubic-bezier(0.4, 0, 0.2, 1),
                border-color 120ms cubic-bezier(0.4, 0, 0.2, 1);
  }
  :global(.app-search-row:hover),
  :global(.app-search-row:focus-visible) {
    background: color-mix(in oklab, var(--foreground) 4%, transparent);
    border-color: color-mix(in oklab, var(--foreground) 12%, transparent);
    outline: none;
  }
  /* Keyboard-active row — drives the ↑/↓ highlight even before the
     pointer is involved. Pairs the brand-green border with a soft
     wash so it reads as "this is what ↵ will open". */
  :global(.app-search-row-active) {
    background: color-mix(in oklab, var(--brand-green, oklch(0.7 0.18 145)) 10%, transparent);
    border-color: color-mix(in oklab, var(--brand-green, oklch(0.7 0.18 145)) 45%, transparent);
  }
  /* Suggestion popover sits inline beneath the SearchBar — same vibe
     as the standalone search demo, just stacked instead of absolute. */
  :global(.app-search-suggestions) {
    margin-top: -6px;
  }
  :global(.app-search-row-kind) {
    font-family: var(--font-mono);
    font-size: 9.5px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--muted-foreground);
  }
  :global(.app-search-row-name) {
    font-family: var(--font-sans);
    font-size: 13px;
    color: var(--foreground);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  :global(.app-search-row-meta) {
    font-family: var(--font-mono);
    font-size: 11px;
    color: var(--muted-foreground);
    letter-spacing: 0.02em;
  }
  :global(.app-search-row-status) {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-family: var(--font-mono);
    font-size: 10px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--muted-foreground);
  }
  :global(.app-search-empty) {
    padding: 20px 10px;
    font-family: var(--font-mono);
    font-size: 11.5px;
    letter-spacing: 0.04em;
    color: var(--muted-foreground);
    text-align: center;
  }
  :global(.app-search-foot) {
    display: flex;
    align-items: center;
    gap: 14px;
    padding-top: 10px;
    border-top: 1px dashed color-mix(in oklab, var(--foreground) 8%, transparent);
    font-family: var(--font-mono);
    font-size: 10.5px;
    color: var(--muted-foreground);
    letter-spacing: 0.02em;
  }

  .app-sb-section-label {
    font-family: var(--font-mono);
    font-size: 9.5px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--muted-foreground);
    padding: 6px 8px 4px;
  }
  /* Interactive variant of the section label — used by the Storage
     subsection. Resets <button> chrome to match the static label, adds
     a chevron on the right that rotates with the open state. */
  .app-sb-section-toggle {
    margin-top: 6px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    background: transparent;
    border: 0;
    cursor: pointer;
    text-align: left;
    transition: color 140ms cubic-bezier(0.4, 0, 0.2, 1);
  }
  .app-sb-section-toggle:hover {
    color: var(--foreground);
  }
  .app-sb-section-toggle:focus-visible {
    outline: 1px solid color-mix(in oklab, var(--foreground) 30%, transparent);
    outline-offset: 2px;
    border-radius: 2px;
  }
  .app-sb-section-chevron {
    transition: transform 160ms cubic-bezier(0.4, 0, 0.2, 1);
    opacity: 0.7;
  }
  .app-sb-section-chevron-open {
    transform: rotate(-180deg);
  }
  .app-sb-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 1px;
  }
  .app-sb-item {
    display: inline-flex;
    width: 100%;
    align-items: center;
    gap: 10px;
    padding: 6px 8px;
    border-radius: 4px;
    color: var(--muted-foreground);
    text-decoration: none;
    font-family: var(--font-sans);
    font-size: 13px;
    line-height: 1.1;
    transition: background 140ms cubic-bezier(0.4, 0, 0.2, 1),
                color 140ms cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
  }
  .app-sb-item:hover {
    background: color-mix(in oklab, var(--foreground) 4%, transparent);
    color: var(--foreground);
  }
  .app-sb-item-active {
    background: color-mix(in oklab, var(--foreground) 6%, transparent);
    color: var(--foreground);
  }
  /* Brand-green left rail on the active item — mirrors the inset card
     accent the rest of the design system uses. */
  .app-sb-item-active::before {
    content: '';
    position: absolute;
    left: -10px;
    top: 4px;
    bottom: 4px;
    width: 2px;
    background: var(--brand-green, oklch(0.7 0.18 145));
    border-radius: 0 2px 2px 0;
  }
  .app-sb-icon {
    display: inline-flex;
    color: currentColor;
    width: 16px;
    justify-content: center;
  }
  .app-sb-item-active .app-sb-icon {
    color: var(--brand-green, oklch(0.7 0.18 145));
  }
  .app-sb-label {
    flex: 1;
  }

  .app-sb-spacer { flex: 1; }

  /* User row wrapper — dashed top separator, then a full-width UserNav
     trigger so the avatar / name / email row fills the sidebar. */
  .app-sb-user {
    padding-top: 10px;
    margin-top: 8px;
    border-top: 1px dashed color-mix(in oklab, var(--foreground) 8%, transparent);
  }
  /* UserNav defaults to inline-block; in the sidebar we want it to span
     the full width so it lines up with the workspace switcher above. */
  :global(.app-sb-user .app-sb-user-trigger) {
    display: block;
    width: 100%;
  }
  :global(.app-sb-user .app-sb-user-trigger > button) {
    display: flex;
    width: 100%;
  }
  :global(.app-sb-user .app-sb-user-trigger .hk-un-meta) {
    flex: 1;
    min-width: 0;
  }
  :global(.app-sb-user .app-sb-user-trigger .hk-un-email) {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* ── Popover menus shared by TeamSwitcher + UserNav ──────────────── */
  .app-sb-menu {
    display: flex;
    flex-direction: column;
    padding: 4px;
    min-width: 200px;
  }
  .app-sb-menu-label {
    font-family: var(--font-mono);
    font-size: 9.5px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--muted-foreground);
    padding: 6px 8px 4px;
  }
  .app-sb-menu-item {
    appearance: none;
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 6px 8px;
    background: transparent;
    border: none;
    border-radius: 3px;
    color: var(--foreground);
    text-align: left;
    cursor: pointer;
    font-family: var(--font-sans);
    font-size: 12.5px;
    transition: background 140ms cubic-bezier(0.4, 0, 0.2, 1);
  }
  .app-sb-menu-item:hover {
    background: color-mix(in oklab, var(--foreground) 6%, transparent);
  }
  .app-sb-menu-avatar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border: 1px solid color-mix(in oklab, var(--foreground) 14%, transparent);
    background: color-mix(in oklab, var(--foreground) 5%, transparent);
    border-radius: 3px;
    font-family: var(--font-mono);
    font-size: 9.5px;
    color: var(--foreground);
    flex-shrink: 0;
    overflow: hidden;
  }
  .app-sb-menu-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  .app-sb-menu-meta {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
  }
  .app-sb-menu-name {
    font-size: 12.5px;
    color: var(--foreground);
    line-height: 1.1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .app-sb-menu-plan {
    font-family: var(--font-mono);
    font-size: 9px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--muted-foreground);
    margin-top: 2px;
  }
  .app-sb-menu-active {
    color: var(--brand-green, oklch(0.7 0.18 145));
    font-size: 10px;
  }
  .app-sb-menu-row {
    justify-content: space-between;
  }
  .app-sb-menu-kbd {
    font-family: var(--font-mono);
    font-size: 9.5px;
    color: var(--muted-foreground);
    letter-spacing: 0.04em;
  }
  .app-sb-menu-sep {
    height: 1px;
    background: color-mix(in oklab, var(--foreground) 8%, transparent);
    margin: 4px 0;
  }
  .app-sb-menu-cta {
    color: var(--muted-foreground);
    font-family: var(--font-mono);
    font-size: 11px;
    letter-spacing: 0.04em;
  }
  .app-sb-menu-cta:hover { color: var(--foreground); }
  .app-sb-menu-plus {
    width: 22px;
    text-align: center;
    color: var(--brand-green, oklch(0.7 0.18 145));
    font-family: var(--font-mono);
  }

  /* ── Inset content card ─────────────────────────────────────────── */
  .app-inset {
    flex: 1;
    min-width: 0;
    margin: 8px;
    border: 1px solid var(--sidebar-border, color-mix(in oklab, var(--foreground) 8%, transparent));
    border-radius: 8px;
    background: var(--sidebar-inset-bg, #040404);
    /* Theme-aware lift — same fix as SidebarInset. Pure-black at 40%
       alpha rendered as a hard near-black line in light mode; mixing
       against `--foreground` keeps the lift proportional in both
       schemes (soft halo on dark, faint smudge on light). */
    box-shadow:
      0 1px 0 color-mix(in oklab, var(--foreground) 6%, transparent),
      0 10px 24px -18px color-mix(in oklab, var(--foreground) 30%, transparent);
    overflow: auto;
    /* The shell is 100vh; the inset scrolls internally. */
    min-height: 0;
  }
  .app-inset-inner {
    padding: 36px 36px;
    /* Pages whose top-level wrapper sets its own height (run-overview's
       `.ro-shell` uses `height: calc(100vh - 96px)`) need the inset
       inner to behave like a flex container so the wrapper can fill. */
    display: flex;
    flex-direction: column;
    min-height: 100%;
  }
  /* Flush mode — drop the lifted-card chrome so content extends from
     the sidebar edge to the viewport edge with no margin/border. The
     run-overview page uses this so its DAG / Inspector / Logs panels
     control their own internal padding without an outer card frame. */
  .app-inset-flush {
    margin: 0;
    border: none;
    border-radius: 0;
    box-shadow: none;
  }
  .app-inset-flush .app-inset-inner {
    padding: 0;
    min-height: 100%;
    height: 100%;
  }

  /* ── Mobile top bar ──────────────────────────────────────────────
     Hidden on desktop; the @media (max-width: 900px) block below flips
     it to display: flex. Lives at the top of `.app-frame` so it sits
     above `.app-shell` (which becomes column-flex on mobile) without
     scrolling away with the page content. */
  .app-mobile-topbar {
    display: none;
    /* On mobile the whole page scrolls (`.app-frame { height: auto }`
       below) rather than scrolling inside the inset card. Pin the
       topbar to the viewport top so the hamburger + brand + search
       trigger stay reachable as the user scrolls long pages. Z-index
       lifts above page content but stays well below the Drawer scrim
       (z-1000) so the open drawer still covers the bar. */
    position: sticky;
    top: 0;
    z-index: 10;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    border-bottom: 1px solid var(--sidebar-border, color-mix(in oklab, var(--foreground) 8%, transparent));
    background: var(--sidebar);
    color: var(--foreground);
  }
  .app-mobile-topbar-btn {
    appearance: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    background: transparent;
    border: 1px solid color-mix(in oklab, var(--foreground) 12%, transparent);
    border-radius: 4px;
    color: var(--foreground);
    cursor: pointer;
    transition: background 140ms cubic-bezier(0.4, 0, 0.2, 1),
                border-color 140ms cubic-bezier(0.4, 0, 0.2, 1);
  }
  .app-mobile-topbar-btn:hover {
    background: color-mix(in oklab, var(--foreground) 6%, transparent);
    border-color: color-mix(in oklab, var(--foreground) 22%, transparent);
  }
  .app-mobile-topbar-btn:focus-visible {
    outline: 2px solid var(--brand-green, oklch(0.7 0.18 145));
    outline-offset: 2px;
  }
  .app-mobile-topbar-brand {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    min-width: 0;
    color: var(--foreground);
    text-decoration: none;
    padding: 4px 6px;
  }
  :global(.app-mobile-topbar-logo) {
    width: 22px;
    height: 22px;
    flex-shrink: 0;
  }
  .app-mobile-topbar-name {
    font-family: var(--font-sans);
    font-size: 15px;
    font-weight: 600;
    letter-spacing: -0.02em;
    line-height: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* ── Mobile nav drawer body ──────────────────────────────────────
     Drawer is portalled outside `.app-frame`, so its inner styles need
     `:global(…)` to escape Svelte's scoped-CSS hashing. The Drawer
     component supplies its own scrim / header / scroll container; we
     just style the nav list inside the body slot. */
  :global(.app-mobile-nav) {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  :global(.app-mobile-nav-eyebrow) {
    font-family: var(--font-mono);
    font-size: 9.5px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--muted-foreground);
    padding: 14px 8px 4px;
  }
  :global(.app-mobile-nav-link) {
    appearance: none;
    display: inline-flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 10px 8px;
    background: transparent;
    border: 0;
    border-radius: 4px;
    color: var(--muted-foreground);
    text-decoration: none;
    font-family: var(--font-sans);
    font-size: 14px;
    line-height: 1.1;
    text-align: left;
    cursor: pointer;
    position: relative;
    transition: background 140ms cubic-bezier(0.4, 0, 0.2, 1),
                color 140ms cubic-bezier(0.4, 0, 0.2, 1);
  }
  :global(.app-mobile-nav-link:hover) {
    background: color-mix(in oklab, var(--foreground) 4%, transparent);
    color: var(--foreground);
  }
  :global(.app-mobile-nav-link-active) {
    background: color-mix(in oklab, var(--foreground) 6%, transparent);
    color: var(--foreground);
  }
  :global(.app-mobile-nav-link-active::before) {
    content: '';
    position: absolute;
    left: -10px;
    top: 6px;
    bottom: 6px;
    width: 2px;
    background: var(--brand-green, oklch(0.7 0.18 145));
    border-radius: 0 2px 2px 0;
  }
  :global(.app-mobile-nav-icon) {
    display: inline-flex;
    color: currentColor;
    width: 18px;
    justify-content: center;
  }
  :global(.app-mobile-nav-link-active .app-mobile-nav-icon) {
    color: var(--brand-green, oklch(0.7 0.18 145));
  }
  :global(.app-mobile-nav-link-row) {
    justify-content: space-between;
  }
  :global(.app-mobile-nav-link-kbd) {
    font-family: var(--font-mono);
    font-size: 10px;
    letter-spacing: 0.04em;
    color: var(--muted-foreground);
    padding: 1px 6px;
    border: 1px solid color-mix(in oklab, var(--foreground) 12%, transparent);
    border-radius: 3px;
    background: var(--background);
  }
  :global(.app-mobile-nav-team-name) {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  :global(.app-mobile-nav-team-plan) {
    font-family: var(--font-mono);
    font-size: 9px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--muted-foreground);
  }

  /* ── Responsive ─────────────────────────────────────────────────── */
  @media (max-width: 900px) {
    .app-shell {
      flex-direction: column;
    }
    /* Reveal the mobile top bar — it replaces the (now-hidden) workspace
       sidebar as the entry point to nav, search, and the drawer. */
    .app-mobile-topbar { display: flex; }
    .app-inset { margin: 0; border-radius: 0; border: none; box-shadow: none; }
    .app-inset-inner { padding: 24px 16px; }
    /* Phone-sized canvases need the page to scroll as a single document,
       not within a nested 100vh inset card. Drop the height cap +
       internal vertical scroll so embedded routes (e.g. run-overview's
       stacked DAG / Inspector / Log) can grow naturally and the browser
       handles the scroll. Keep horizontal overflow clipped so any
       wide-by-design child (xyflow viewport, etc.) can't punch through
       the viewport and pollute the page's scroll width. */
    .app-frame { height: auto; }
    .app-shell { min-height: 100vh; }
    .app-inset {
      /* Keep `flex: 1` (don't switch to `flex: none`) so the inset is
       * bound to the column's intrinsic width rather than stretching to
       * its content — otherwise wide-by-design children (the xyflow
       * viewport, the Splitter's absolute panes) make the inset balloon
       * past the viewport. */
      width: 100%;
      max-width: 100%;
      overflow-x: hidden;
      overflow-y: visible;
      min-height: 0;
    }
    .app-inset-inner {
      width: 100%;
      max-width: 100%;
      min-height: 0;
    }
  }
</style>
